import type * as MB from "~/types/MailBlocks";
import { v4 } from "uuid";
import type { Attribute, JSONContent } from "@tiptap/core";

export type JSONContentBlock<Attributes extends MB.Blocks> = Attributes &
    JSONContent & {
        type: Attributes["type"];
        attrs: Omit<Attributes, "type">;
    };

export function createEmptyBlock(type: MB.BlockType): MB.Block<typeof type> {
    return {
        type,
        id: v4(),
        children: [],
    };
}

export function blockify(block: any): MB.Blocks | undefined {
    if (!block) return undefined;
    let blockifiedBlock;

    if (block.type === "text") {
        blockifiedBlock = {
            text: block.text,
        };
    } else if (block.type === "document") {
        blockifiedBlock = {
            ...block.value,
            type: block.type,
            children: block.content?.map((child: any) => blockify(child)) ?? [],
        };
    } else if ("content" in block) {
        blockifiedBlock = {
            type: block.type,
            ...block.attrs,
            children: block.content?.map((child: any) => blockify(child)) ?? [],
        };
    } else {
        blockifiedBlock = {
            type: block.type,
            ...block.attrs,
        };
    }

    if ("marks" in block) {
        blockifiedBlock.marks = block.marks;
    }

    return blockifiedBlock;
}

export function tiptapify(block: MB.Blocks): JSONContent | undefined {
    if (!block) return undefined;
    if ("text" in block) {
        if (typeof block.text !== "string") return undefined;

        const newBlock: JSONContent = {
            type: "text",
            text: block.text,
        };

        if (
            "marks" in block &&
            Array.isArray(block.marks) &&
            block.marks.length
        ) {
            newBlock.marks = block.marks;
        }

        return newBlock;
    } else if ("children" in block) {
        const type = block.type;
        const content: any[] = block.children
            .map((child: any) => tiptapify(child as MB.Blocks))
            .filter((child: any) => !!child);

        delete (block as Partial<MB.Blocks> & { children?: any[] }).children;
        delete (block as Partial<MB.Blocks>).type;

        if (content.length === 0) {
            return {
                type,
                attrs: {
                    ...(block as any),
                },
            };
        }

        return {
            type,
            content,
            attrs: {
                ...(block as any),
            },
        };
    } else {
        const type = block.type;
        delete (block as Partial<MB.Blocks>).type;
        const newBlock: JSONContent = {
            type,
            attrs: {
                ...block,
            },
        };

        if (
            "marks" in block &&
            Array.isArray(block.marks) &&
            block.marks.length
        ) {
            newBlock.marks = block.marks;
        }

        return newBlock;
    }
}

function cleanOptions(
    options: Partial<Omit<MB.Blocks, "type">>,
    type: "regular" | "tiptap",
) {
    if (type === "regular" && "attrs" in options) {
        options = blockify(options);
    }

    return options;
}

export function createDocumentBlock(
    options: Partial<Omit<MB.DocumentBlock, "type">> = {},
): MB.DocumentBlock {
    options = cleanOptions(options, "regular");
    return {
        type: "document",
        id: v4(),
        identifier: options.identifier ?? "",
        theme: options.theme ?? ({} as MB.DocumentBlock["theme"]),
        children: options.children ?? [],
        backgroundColor:
            options.backgroundColor ??
            "{{brand.theme.palette.background.color}}",
        linkColor: options.linkColor ?? "{{brand.theme.palette.primary.color}}",
        linkUnderlined: options.linkUnderlined ?? true,
    };
}

export function createSectionBlock(
    options: Partial<Omit<MB.SectionBlock, "type">> = {},
): MB.SectionBlock {
    options = cleanOptions(options, "regular");
    return {
        type: "section",
        id: v4(),
        identifier: options.identifier ?? "",
        theme: options.theme ?? ({} as MB.SectionBlock["theme"]),
        children: options.children ?? [createParagraphBlock()],
        backgroundColor:
            options.backgroundColor ?? "{{brand.theme.palette.surface.color}}",
        backgroundImage: options.backgroundImage,
        borderColor: options.borderColor,
        borderTopLeftRadius: options.borderTopLeftRadius ?? "0px",
        borderBottomLeftRadius: options.borderBottomLeftRadius ?? "0px",
        borderTopRightRadius: options.borderTopRightRadius ?? "0px",
        borderBottomRightRadius: options.borderBottomRightRadius ?? "0px",
        borderTopWidth: options.borderTopWidth ?? "0px",
        borderBottomWidth: options.borderBottomWidth ?? "0px",
        borderLeftWidth: options.borderLeftWidth ?? "0px",
        borderRightWidth: options.borderRightWidth ?? "0px",
        marginBottom: options.marginBottom ?? "0px",
        marginTop: options.marginTop ?? "50px",
        paddingTop: options.paddingTop ?? "30px",
        paddingBottom: options.paddingBottom ?? "10px",
        paddingLeft: options.paddingLeft ?? "20px",
        paddingRight: options.paddingRight ?? "20px",
        width: options.width ?? "100%",
    };
}

export function createLayoutBlock(
    options: Partial<Omit<MB.LayoutBlock, "type">> = {},
): MB.LayoutBlock {
    return {
        ...createEmptyBlock("layout"),
        ...options,
    } as unknown as MB.LayoutBlock;
}

export function createColumnBlock(
    options: Partial<Omit<MB.ColumnItemBlock, "type">> = {},
): MB.ColumnItemBlock {
    return {
        type: "column",
        id: v4(),
        children: options.children ?? [
            createParagraphBlock({
                children: [{ text: "Column" }],
            }),
        ],
    } as unknown as MB.ColumnItemBlock;
}

export function createColumnsBlock(
    options: Partial<Omit<MB.ColumnsBlock, "type">> = {},
): MB.ColumnsBlock {
    return {
        type: "columns",
        id: v4(),
        children: options.children ?? [
            createColumnBlock(),
            createColumnBlock(),
        ],
        gap: options.gap ?? 0,
        layout: options.layout ?? "two-column-equal",
        paddingTop: options.paddingTop ?? "0px",
        paddingBottom: options.paddingBottom ?? "0px",
        paddingLeft: options.paddingLeft ?? "0px",
        paddingRight: options.paddingRight ?? "0px",
        backgroundColor:
            options.backgroundColor ?? "{{brand.theme.palette.surface.color}}",
        marginBottom: options.marginBottom ?? "12px",
        marginTop: options.marginTop ?? "12px",
    } as unknown as MB.ColumnsBlock;
}

export function createSocialsBlock(
    options: Partial<Omit<MB.SocialsBlock, "type">> = {},
): MB.SocialsBlock {
    return {
        type: "socials",
        id: v4(),
        children: options.children ?? [
            createSocialBlock(),
            createSocialBlock(),
        ],
        alignment: options.alignment ?? "center",
        marginBottom: options.marginBottom ?? "0px",
        marginTop: options.marginTop ?? "0px",
        iconSize: options.iconSize ?? "20px",
        fontSize: options.fontSize ?? "13px",
        mode: options.mode ?? "horizontal",
        textColor:
            options.textColor ?? "{{brand.theme.palette.surface.text_color}}",
    } as unknown as MB.SocialsBlock;
}

export function createSocialBlock(
    options: Partial<Omit<MB.SocialBlock, "type">> = {},
): MB.SocialBlock {
    return {
        type: "social",
        id: v4(),
        children: options.children ?? [],
        url: options.url ?? "",
        src: options.src ?? "",
    } as unknown as MB.SocialBlock;
}

export function createParagraphBlock(
    options: Partial<Omit<MB.ParagraphBlock, "type">> = {},
): MB.ParagraphBlock {
    options = cleanOptions(options, "regular");
    return {
        type: "paragraph",
        id: v4(),
        children: options.children ?? [],
        fontFamily:
            options.fontFamily ??
            "{{brand.theme.typography.default.font_family}}",
        fontSize: options.fontSize ?? "16px",
        fontWeight:
            options.fontWeight ??
            "{{brand.theme.typography.default.font_weight}}",
        letterSpacing: options.letterSpacing ?? '0px',
        lineHeight: options.lineHeight ?? 1,
        linkColor: options.linkColor ?? "inherit",
        linkUnderlined: options.linkUnderlined ?? "inherit",
        marginBottom: options.marginBottom ?? "10px",
        marginTop: options.marginTop,
        alignment: options.alignment ?? "left",
        textColor:
            options.textColor ?? "{{brand.theme.palette.surface.text_color}}",
    };
}

export function createHtmlBlock(
    options: Partial<Omit<MB.HtmlBlock, "type">> = {},
): MB.HtmlBlock {
    options = cleanOptions(options, "regular");

    return {
        id: v4(),
        type: "html",
        html: options.html ?? "",
    };
}

export function createFormBlock(
    options: Partial<Omit<MB.FormBlock, "type">> = {},
): MB.FormBlock {
    options = cleanOptions(options, "regular");
    return {
        type: "form",
        id: v4(),
        borderBottomLeftRadius: options.borderBottomLeftRadius ?? "8px",
        borderBottomRightRadius: options.borderBottomRightRadius ?? "8px",
        borderTopLeftRadius: options.borderTopLeftRadius ?? "8px",
        borderTopRightRadius: options.borderTopRightRadius ?? "8px",
        preview: options?.preview ?? "button",
        children: options.children ?? [{ text: "Give Feedback" }],
        textColor:
            options?.textColor ?? "{{brand.theme.palette.primary.text_color}}",
        backgroundColor:
            options?.backgroundColor ?? "{{brand.theme.palette.primary.color}}",
        fontFamily:
            options.fontFamily ??
            "{{brand.theme.typography.default.font_family}}",
        fontSize: options.fontSize ?? "16px",
        fontWeight:
            options.fontWeight ??
            "{{brand.theme.typography.default.font_weight}}",
        letterSpacing: options.letterSpacing,
        lineHeight: options.lineHeight,
        marginBottom: options.marginBottom ?? "12px",
        marginTop: options.marginTop ?? "12px",
        width: options.width ?? "100%",
        size: options.size ?? "sm",
        showQuestion: options.showQuestion ?? true,
        alignment: options.alignment ?? "center",
        paddingTop: options.paddingTop ?? "12px",
        paddingBottom: options.paddingBottom ?? "12px",
        paddingLeft: options.paddingLeft ?? "20px",
        paddingRight: options.paddingRight ?? "20px",
        ratingStart: options.ratingStart ?? 1,
        ratingEnd: options.ratingEnd ?? 10,
        ratingLabelMin: options.ratingLabelMin ?? "",
        ratingLabelMid: options.ratingLabelMid ?? "",
        ratingLabelMax: options.ratingLabelMax ?? "",
        ratingInverted: options.ratingInverted ?? false,
    };
}

export function createButtonBlock(
    options: Partial<Omit<MB.ButtonBlock, "type">> = {},
): MB.ButtonBlock {
    options = cleanOptions(options, "regular");
    return {
        type: "button",
        id: v4(),
        children: options.children ?? [{ text: "Click Me" }],
        alignment: options.alignment ?? "center",
        backgroundColor:
            options.backgroundColor ?? "{{brand.theme.palette.primary.color}}",
        borderBottomLeftRadius: options.borderBottomLeftRadius ?? "8px",
        borderBottomRightRadius: options.borderBottomRightRadius ?? "8px",
        borderBottomWidth: options.borderBottomWidth,
        borderLeftWidth: options.borderLeftWidth,
        borderRightWidth: options.borderRightWidth,
        borderTopLeftRadius: options.borderTopLeftRadius ?? "8px",
        borderTopRightRadius: options.borderTopRightRadius ?? "8px",
        borderTopWidth: options.borderTopWidth,
        fontFamily:
            options.fontFamily ??
            "{{brand.theme.typography.default.font_family}}",
        fontSize: options.fontSize ?? "16px",
        fontWeight:
            options.fontWeight ??
            "{{brand.theme.typography.default.font_weight}}",
        letterSpacing: options.letterSpacing,
        lineHeight: options.lineHeight,
        marginBottom: options.marginBottom,
        marginTop: options.marginTop,
        textColor:
            options.textColor ?? "{{brand.theme.palette.primary.text_color}}",
        url: options.url,
        width: options.width ?? "auto",
        size: options.size ?? "md",
        paddingTop: options.paddingTop ?? "12px",
        paddingBottom: options.paddingBottom ?? "12px",
        paddingLeft: options.paddingLeft ?? "20px",
        paddingRight: options.paddingRight ?? "20px",
    };
}

export function createButtonGroupBlock(
    options: Partial<Omit<MB.ButtonGroupBlock, "type">> = {},
): MB.ButtonGroupBlock {
    return {
        ...createEmptyBlock("button-group"),
        ...options,
    } as unknown as MB.ButtonGroupBlock;
}

export function createBlockQuoteBlock(
    options: Partial<Omit<MB.BlockQuoteBlock, "type">> = {},
): MB.BlockQuoteBlock {
    return {
        ...createEmptyBlock("blockquote"),
        ...options,
    } as unknown as MB.BlockQuoteBlock;
}

export function createHeadingBlock(
    options: Partial<Omit<MB.HeadingBlock, "type">> = {},
): MB.HeadingBlock {
    options = cleanOptions(options, "regular");
    return {
        type: "heading",
        id: v4(),
        level: options.level ?? 1,
        textTransform: options.textTransform ?? "none",
        children: options.children ?? [],
        fontFamily:
            options.fontFamily ??
            "{{brand.theme.typography.headings.font_family}}",
        fontSize:
            options.fontSize ??
            {
                1: `32px`,
                2: `24px`,
                3: `19px`,
                4: `16px`,
                5: `13px`,
                6: `11px`,
            }[options.level ?? 1],
        alignment: options.alignment ?? "left",
        fontWeight:
            options.fontWeight ??
            "{{brand.theme.typography.headings.font_weight}}",
        letterSpacing: options.letterSpacing ?? '0px',
        lineHeight: options.lineHeight ?? 1,
        linkColor: options.linkColor ?? "inherit",
        linkUnderlined: options.linkUnderlined ?? "inherit",
        marginBottom: options.marginBottom,
        marginTop: options.marginTop,
        textColor:
            options.textColor ?? "{{brand.theme.palette.surface.text_color}}",
    } as unknown as MB.HeadingBlock;
}

export function createNumberListBlock(
    options: Partial<Omit<MB.NumberListBlock, "type">> = {},
): MB.NumberListBlock {
    return {
        ...createEmptyBlock("number-list"),
        ...options,
    } as unknown as MB.NumberListBlock;
}

export function createBulletListBlock(
    options: Partial<Omit<MB.BulletListBlock, "type">> = {},
): MB.BulletListBlock {
    return {
        ...createEmptyBlock("bullet-list"),
        ...options,
    } as unknown as MB.BulletListBlock;
}

export function createImageBlock(
    options: Partial<Omit<MB.ImageBlock, "type">> = {},
): MB.ImageBlock {
    options = cleanOptions(options, "regular");
    return {
        type: "image",
        id: v4(),
        children: options?.children ?? [],
        src: options?.src as MB.ImageBlock["src"],
        alt: options?.alt ?? "",
        height: options?.height ?? "auto",
        fullWidth: options?.fullWidth ?? true,
        caption: options?.caption ?? false,
        alignment: options?.alignment ?? "center",
        marginBottom: options?.marginBottom ?? "0px",
        marginTop: options?.marginTop ?? "0px",
        borderTopLeftRadius: options.borderTopLeftRadius ?? "0px",
        borderBottomLeftRadius: options.borderBottomLeftRadius ?? "0px",
        borderTopRightRadius: options.borderTopRightRadius ?? "0px",
        borderBottomRightRadius: options.borderBottomRightRadius ?? "0px",
    };
}

export function createSignatureBlock(
    options: Partial<Omit<MB.SignatureBlock, "type">> = {},
): MB.SignatureBlock {
    options = cleanOptions(options, "regular");
    return {
        type: "signature",
        id: v4(),
        variant: "2-column-image",
        children: options?.children ?? [
            createParagraphBlock(),
            createParagraphBlock(),
            createParagraphBlock(),
        ],
        src: options?.src as MB.SignatureBlock["src"],
        alt: options?.alt ?? "",
        marginBottom: options?.marginBottom ?? "0px",
        marginTop: options?.marginTop ?? "0px",
    };
}

export function createGalleryBlock(
    options: Partial<Omit<MB.GalleryBlock, "type">> = {},
): MB.GalleryBlock {
    return {
        ...createEmptyBlock("gallery"),
        ...options,
    } as unknown as MB.GalleryBlock;
}

export function createVideoBlock(
    options: Partial<Omit<MB.VideoBlock, "type">> = {},
): MB.VideoBlock {
    return {
        ...createEmptyBlock("video"),
        ...options,
    } as unknown as MB.VideoBlock;
}

export function createTimerBlock(
    options: Partial<Omit<MB.TimerBlock, "type">> = {},
): MB.TimerBlock {
    return {
        ...createEmptyBlock("timer"),
        ...options,
    } as unknown as MB.TimerBlock;
}

export function createBlock(
    type: MB.BlockType,
    options: Partial<Omit<MB.Blocks, "type">> = {},
): MB.Blocks {
    const store = useMailStore();

    if (Object.keys(options ?? {}).length === 0 && store.focusedJsonId) {
        options =
            (
                store.getThemeForId(store.focusedJsonId) as Record<
                    MB.BlockType,
                    Partial<Omit<MB.Blocks, "type">>
                >
            )[type] ?? {};
    }

    switch (type) {
        case "document":
            return createDocumentBlock(options);
        case "section":
            return createSectionBlock(options);
        case "layout":
            return createLayoutBlock(options);
        case "columns":
            return createColumnsBlock(options);
        case "column":
            return createColumnBlock(options);
        case "socials":
            return createSocialsBlock(options);
        case "social":
            return createSocialBlock(options);
        case "paragraph":
            return createParagraphBlock(options);
        case "html":
            return createHtmlBlock(options);
        case "form":
            return createFormBlock(options);
        case "signature":
            return createSignatureBlock(options);
        case "button":
            return createButtonBlock(options);
        case "button-group":
            return createButtonGroupBlock(options);
        case "blockquote":
            return createBlockQuoteBlock(options);
        case "heading":
            return createHeadingBlock(options);
        case "number-list":
            return createNumberListBlock(options);
        case "bullet-list":
            return createBulletListBlock(options);
        case "image":
            return createImageBlock(options);
        case "gallery":
            return createGalleryBlock(options);
        case "video":
            return createVideoBlock(options);
        case "timer":
            return createTimerBlock(options);
    }
}

export function createBlockForTipTap(
    type: MB.BlockType,
    options: Partial<Omit<MB.Blocks, "type">> = {},
) {
    const block = createBlock(type, options) as Partial<MB.Blocks> & {
        path: string;
    };
    delete block.type;

    if ("children" in block) {
        delete block.children;
    }

    return block;
}

export function createDefaultBlockForTipTap(
    type: MB.BlockType,
    options: Partial<Omit<MB.Blocks, "type">> = {},
): Record<string, Attribute> {
    const block = createBlock(type, options) as Partial<MB.Blocks>;
    delete block.type;

    if ("children" in block) {
        delete block.children;
    }

    return Object.fromEntries(
        Object.entries(block).map(([key, value]) => {
            return [
                key,
                {
                    default: value,
                    rendered: true,
                    renderHTML: (attributes) => {
                        if (key === "id") {
                            return {
                                ["data-id"]: v4(),
                            };
                        }

                        let value = attributes[key];

                        if (Array.isArray(value) || typeof value === "object") {
                            value = JSON.stringify(value);
                        }

                        return {
                            [`data-${key.toLowerCase()}`]: value,
                        };
                    },
                    parseHTML: (element) => {
                        if (key === "id") {
                            return v4();
                        }

                        const value = element.getAttribute(
                            `data-${key.toLowerCase()}`,
                        );

                        try {
                            return typeof value === "string"
                                ? JSON.parse(value ?? "")
                                : value;
                        } catch {
                            return value;
                        }
                    },
                } as Attribute,
            ];
        }),
    );
}
